import React, { forwardRef, useState, useEffect } from 'react';
import { ErpFormInput, ErpFormInputProps } from './ErpFormInput';
import BigNumber from 'bignumber.js';

export interface ErpNumberInputProps extends Omit<ErpFormInputProps, 'onChange'> {
  value?: string | number | null;
  onChange?: (value: string) => void;
  precision?: number;
  min?: number;
  max?: number;
  allowNegative?: boolean;
}

const _ErpNumberInput = forwardRef<HTMLInputElement, ErpNumberInputProps>(({
  value = '',
  onChange,
  precision = 4, // BCMath standard 4
  min,
  max,
  allowNegative = false,
  className = '',
  onBlur,
  ...props
}, ref) => {
  // Same normalisation the initial state uses, so a non-numeric or malformed
  // prop is never displayed verbatim and then reformatted on blur.
  const normalise = (raw: string | number | null | undefined) => {
    if (raw === null || raw === undefined || raw === '') return '';
    const bn = new BigNumber(raw);
    return bn.isNaN() ? '' : raw.toString();
  };

  const [localValue, setLocalValue] = useState<string>(normalise(value));

  useEffect(() => {
    setLocalValue(normalise(value));
  }, [value]);

  const handleChange = (val: any) => {
    let str = String(val);
    
    // Allow typing minus sign if negative is allowed
    if (allowNegative && str === '-') {
      setLocalValue(str);
      return;
    }

    // Keep a leading minus only when negatives are allowed, then strip all other non-numeric chars
    const hasLeadingMinus = allowNegative && str.startsWith('-');
    str = str.replace(/[^\d.]/g, '');
    str = (hasLeadingMinus ? '-' : '') + str;

    // Merge extra decimal separators before enforcing precision, including paste.
    const [integer, ...fraction] = str.split('.');
    if (fraction.length) {
      str = integer + '.' + fraction.join('').slice(0, precision);
    }

    setLocalValue(str);
    
    if (str && str !== '-') {
      const bn = new BigNumber(str);
      if (!bn.isNaN()) {
        onChange?.(str);
      }
    } else if (str === '') {
      onChange?.('');
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (localValue && localValue !== '-') {
      let bn = new BigNumber(localValue);
      if (!bn.isNaN()) {
        if (min !== undefined && bn.lt(min)) bn = new BigNumber(min);
        if (max !== undefined && bn.gt(max)) bn = new BigNumber(max);
        
        const formatted = bn.toFixed(precision, BigNumber.ROUND_HALF_UP);
        setLocalValue(formatted);
        onChange?.(formatted);
      } else {
        setLocalValue('');
        onChange?.('');
      }
    } else {
      setLocalValue('');
      onChange?.('');
    }
    onBlur?.(e);
  };

  return (
    <ErpFormInput
      ref={ref}
      {...props}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className={`text-end ${className}`.trim()}
      inputMode="decimal"
      dir="ltr"
    />
  );
});

export const ErpNumberInput = React.memo(_ErpNumberInput);
export const ErpCurrencyInput = ErpNumberInput; // Same logic for now, just 4-8 decimals natively
